import { useMemo } from 'react';

import { toError } from 'utils/errors';
import logger from 'utils/logger';

import type { ICmsQuestCard, ICmsQuestCardUI, ICmsQuestDetail } from '../cms-hooks/types';
import { getLinkText } from '../custom-text';
import type { IQuest } from '../loyalty-quest/types';
import { isIncentiveRedeemed, isQuestCompleted } from '../loyalty-quest/utils';

import type { IUseParseQuestCardData } from './types';
import {
  formatCompletedChallengeMessage,
  getActiveTierImage,
  getButtonText,
  getRangeDateDescription,
  getRedeemedDescription,
} from './utils';

// TODO: remove this hook and use the context in the components that need the IQuest.
export const useParseQuestCardData = (
  quest: IQuest | undefined,
  cmsQuestCard: ICmsQuestCard | undefined,
  cmsQuestCardUI: ICmsQuestCardUI | null | undefined,
  cmsQuestDetail: ICmsQuestDetail | null
): IUseParseQuestCardData => {
  const { startDate, endDate, status, incentiveType, lastCompletion } = quest || {};
  const { name } = cmsQuestCard || {};
  const incentiveRedeemed = isIncentiveRedeemed(lastCompletion);

  return useMemo(() => {
    const isCompleted = isQuestCompleted(status);
    const buttonText = getButtonText({ questStatus: status, incentiveRedeemed, cmsQuestCardUI });
    let description = '';

    try {
      if (isCompleted) {
        description = incentiveRedeemed
          ? getRedeemedDescription({ incentiveType, cmsQuestDetail })
          : cmsQuestCardUI?.completedDescription?.locale ?? '';

        description = formatCompletedChallengeMessage({ description, quest });
      } else if (startDate && endDate) {
        description = getRangeDateDescription({ startDate, endDate });
      }
    } catch (e) {
      logger.error(toError(e));
    }

    const { image, imageDescription } = getActiveTierImage(quest, cmsQuestCard, cmsQuestDetail);

    const questSectionData: IUseParseQuestCardData = {
      image,
      imageDescription,
      buttonText,
      title: name?.locale || '',
      description,
      linkText: getLinkText(),
    };

    return questSectionData;
  }, [
    cmsQuestCard,
    cmsQuestCardUI,
    cmsQuestDetail,
    endDate,
    incentiveRedeemed,
    incentiveType,
    name,
    quest,
    startDate,
    status,
  ]);
};
